import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  MapPin,
  Clock,
  Layers,
  PackagePlus,
  Scissors,
  Leaf,
  Recycle,
  TreePine,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de cartons de déménagement {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Le temps presse et les semaines précédant un déménagement
              défilent à toute vitesse. Pour vous éviter des
              allers-retours fatigants dans les zones commerciales des
              Yvelines, notre service de livraison s&apos;invite
              directement chez vous.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une couverture totale",
                body: "Nous desservons l'ensemble des quartiers d'Andrésy — du centre historique jusqu'aux hauteurs de la ville — ainsi que les communes voisines comme Maurecourt, Conflans-Sainte-Honorine et Chanteloup-les-Vignes.",
                Icon: MapPin,
              },
              {
                title: "Un gain de temps précieux",
                body: "Vos cartons vous sont livrés à plat et bien protégés, au moment qui vous arrange le mieux pour vous permettre de commencer vos préparatifs sans attendre.",
                Icon: Clock,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Matériel de protection ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Protection professionnelle
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Matériel de protection professionnel : la sécurité avant
            tout
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un bon carton ne fait pas tout : ce qui se trouve à
            l&apos;intérieur doit être parfaitement immobilisé.
            C&apos;est pourquoi nous proposons une gamme complète de
            matériel de protection de qualité professionnelle.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Films et bulles */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <PackagePlus className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Films et bulles
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nos rouleaux de film à bulles haute densité amortissent
              les moindres chocs, tandis que nos films étirables
              protègent vos meubles de la poussière et des frottements.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Film à bulles haute densité
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Film étirable anti-poussière
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Amortit les chocs et vibrations
              </div>
            </div>
          </div>

          {/* Adhésifs techniques */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Scissors className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Adhésifs techniques
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nos scotchs à forte adhérence sécurisent la fermeture de
              vos colis pour éviter qu&apos;ils ne cèdent sous le poids,
              garantissant ainsi une manipulation totalement sereine
              pour vous ou vos déménageurs.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Adhésifs forte adhérence
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Fermeture hermétique
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Dévidoirs ergonomiques
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-andresy-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Voir film, bulles &amp; adhésifs
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-andresy-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Découvrir nos kits
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Impact environnemental ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Éco-responsable
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Réduire son impact environnemental pendant son déménagement
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que la préservation de notre environnement est une
            priorité, notamment dans une ville aussi liée à la nature
            et au fleuve qu&apos;Andrésy, nous nous engageons pour des
            préparatifs plus écoresponsables.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Éco-conception",
              body: "Nos cartons sont fabriqués à partir de fibres recyclées et sont 100 % biodégradables et recyclables.",
              Icon: Leaf,
            },
            {
              title: "Durabilité",
              body: "Grâce à leur grande robustesse, vous pouvez facilement les réutiliser pour du stockage ou les transmettre à des proches.",
              Icon: Recycle,
            },
            {
              title: "Logistique verte",
              body: "En regroupant vos achats de fournitures et en profitant de notre service de livraison optimisé dans le 78, vous limitez considérablement les déplacements individuels polluants.",
              Icon: TreePine,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-andresy-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-andresy-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-andresy-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
